import EntryActions from 'actions/EntryActions';
import * as analytics from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {COLLABORATION_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import FolderItemState from 'filestate/FolderItemState';
import DocumentActions from 'notebook/documents/DocumentActions';
import DocumentStore from 'notebook/documents/DocumentStore';
import getRealtimeChannel from 'realtime/getRealtimeChannel';
import RealtimeChannel, {
  RealtimeChannelCapability,
  RealtimeError,
  RealtimeMessage,
} from 'realtime/RealtimeChannel';
import {getCollaborationAnalyticsMetadata} from 'realtime/util/analytics/CollaborationAnalyticsMetadata';
import {getRealtimeChannelNameForApiId, isCurrentClient} from 'realtime/util/RealtimeUtil';
import {getStore} from 'redux-stores/Store';
import EntityStore from 'stores/EntityStore';
// @ts-expect-error
import EntryStore from 'stores/EntryStore';
import APIUtil from 'util/APIUtil';
import * as logging from 'util/Logging';
import {Optional} from 'util/Types';

import {getResourceIdForRealtime} from '../../buffers/PresenceManager';
import {ServerDocumentEditorLock} from './DocumentEditorLockConstants';
import {fetchLock} from './DocumentEditorLockDAO';
import {documentEditorLockClear, documentEditorLockUpdate} from './DocumentEditorLockReduxActions';
import {displayNotification} from './DocumentUpdateNotifications';
import {DocumentUpdateData, DocumentUpdateMessageTypes} from './DocumentUpdatesUtils';

export const MAX_DOCUMENT_CONNECTIONS = 20;

interface DocumentConnection {
  fileId: string;
  realtimeChannel: RealtimeChannel;
}

export default class DocumentUpdatesManager {
  activeDocument: Optional<DocumentConnection>;
  allDocumentConnections: Array<DocumentConnection>;

  constructor() {
    this.allDocumentConnections = [];
  }

  changeActiveDocument(fileRef: FolderItemState | string | null): void {
    if (!BenchlingConfig.ELN_ENABLE_REALTIME_UPDATES_M1 || EntityStore.currentUser() == null) {
      return;
    }
    this.markInactive();
    const fileId = typeof fileRef === 'string' ? fileRef : getResourceIdForRealtime(fileRef);
    if (!fileId) {
      return;
    }

    this.allDocumentConnections
      .filter((documentConnection) => documentConnection.fileId === fileId)
      .forEach((documentConnection) => {
        documentConnection.realtimeChannel.stop();
      });
    this.allDocumentConnections = this.allDocumentConnections.filter(
      (documentConnection) => documentConnection.fileId !== fileId
    );

    getRealtimeChannel(getRealtimeChannelNameForApiId(fileId), [
      RealtimeChannelCapability.SUBSCRIBE,
      RealtimeChannelCapability.PRESENCE,
    ])
      .then((realtimeChannel) => {
        this.activeDocument = {
          fileId,
          realtimeChannel,
        };
        realtimeChannel.subscribe(activeMessageHandler(fileId));
        // Make sure we have the current lock state
        fetchAndUpdateFileLockState(fileId);
      })
      .catch((err: Error) => {
        if (err instanceof RealtimeError) {
          logging.warn(`error connecting to realtime channel`, {extra: {err}});
          return;
        }

        throw err;
      });
  }

  markInactive(): void {
    if (!BenchlingConfig.ELN_ENABLE_REALTIME_UPDATES_M1) {
      return;
    }

    if (!this.activeDocument) {
      return;
    }

    if (this.allDocumentConnections.length >= MAX_DOCUMENT_CONNECTIONS) {
      this.allDocumentConnections[0].realtimeChannel.stop();
      this.allDocumentConnections.shift();
    }

    this.activeDocument.realtimeChannel.unsubscribe();
    this.activeDocument.realtimeChannel.subscribe(inactiveMessageHandler(this.activeDocument.fileId));
    this.allDocumentConnections.push(this.activeDocument);
    this.activeDocument = null;
  }
}

function inactiveMessageHandler(fileId: string): (msg: RealtimeMessage<DocumentUpdateData>) => void {
  return (msg: RealtimeMessage<DocumentUpdateData>) => {
    if (
      msg.name === DocumentUpdateMessageTypes.ENTRY_UPDATE &&
      (APIUtil.isEntry(fileId) || APIUtil.isDocument(fileId))
    ) {
      if (msg.data?.clientId && !isCurrentClient(msg.data.clientId)) {
        const Actions = APIUtil.isEntry(fileId) ? EntryActions : DocumentActions;
        Actions.postServerUpdate(fileId, {isStale: true});
      }
    }
  };
}

function activeMessageHandler(fileId: string): (msg: RealtimeMessage<DocumentUpdateData>) => void {
  return (msg: RealtimeMessage<DocumentUpdateData>) => {
    if (msg.data?.userNotifications) {
      msg.data.userNotifications.forEach((notification) => {
        displayNotification(notification);
      });
    }
    if (msg.name === DocumentUpdateMessageTypes.ENTRY_UPDATE) {
      fetchAndUpdateFileLockState(fileId);
      // Ignore the message if it is triggered by the current client since it should already have the latest changes
      if (msg.data?.clientId && !isCurrentClient(msg.data.clientId)) {
        analytics.trackEvent(
          COLLABORATION_ANALYTICS_ACTIONS.RECEIVED_REALTIME_UPDATE,
          getCollaborationAnalyticsMetadata(fileId)
        );
        if (APIUtil.isEntry(fileId) || APIUtil.isDocument(fileId)) {
          const Store = APIUtil.isEntry(fileId) ? EntryStore : DocumentStore;
          if (Store.inStore(fileId)) {
            const editorState = Store.getEditorState(fileId);
            // Don't update the entry if the client is viewing an old version
            if (editorState.openVersionId !== editorState.metadata?.latestVersionId) {
              return;
            }
            // Don't update the entry if the current user has started editing
            if (Store.hasUnsavedChanges(fileId)) {
              return;
            }
          }
          const Actions = APIUtil.isEntry(fileId) ? EntryActions : DocumentActions;
          Actions.open(fileId);
        }
      }
    } else if (msg.name === DocumentUpdateMessageTypes.LOCK_UPDATE) {
      fetchAndUpdateFileLockState(fileId, msg.data?.lock);
    }
  };
}

async function fetchAndUpdateFileLockState(
  fileId: string,
  serverLock?: ServerDocumentEditorLock
): Promise<void> {
  let clientLock: ServerDocumentEditorLock | undefined | null = serverLock;
  if (!clientLock) {
    clientLock = await fetchLock(fileId);
  }
  // If there is a client lock and it isn't expired, update it in the store. Otherwise, clear it.
  if (clientLock && clientLock.msUntilExpiration > 0) {
    getStore().dispatch(
      documentEditorLockUpdate(fileId, clientLock.userId, clientLock.clientId, clientLock.msUntilExpiration)
    );
  } else {
    getStore().dispatch(documentEditorLockClear(fileId, true));
  }
}
